import React, { useState, useEffect, ChangeEvent, FormEvent, useCallback } from 'react';
import { Typography, Button, Divider, Grid, Box } from '@mui/material';
import { ArrowBack, Delete } from '@mui/icons-material';
import useAxios from 'packages/novous_web/src/api/useAxios';
import apiClient from 'packages/novous_web/src/api/apiClient';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import { getAllCountries, getStatesOfCountry, CountryType, StateType } from '../../../../utils/locationUtils';
import { useToast } from '../../../../context/ToastContext';
import DOMPurify from 'dompurify';
import InputField from 'packages/novous_web/src/common/components/InputField';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import file from 'packages/novous_web/src/assets/icons/Image File.svg';
import ArrowBackIcon from 'packages/novous_web/src/assets/icons/ArrowLeft.svg';

type FormData = {
  companyName: string;
  timeZones: string;
  companyType: string;
  crn: string;
  description: string;
  deviceTypes: string;
  landline: string;
  mobile: string;
  email: string;
  website: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  fullAddress: string;
  selectedFile?: File | null;
  previewUrl: string;
};

type Errors = Partial<Record<keyof FormData, string>>;

// Main Component
const CreateCompany: React.FC = () => {
  // State Management
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    timeZones: '',
    companyType: '',
    crn: '',
    description: '',
    deviceTypes: '',
    landline: '',
    mobile: '',
    email: '',
    website: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
    fullAddress: '',
    selectedFile: null,
    previewUrl: '',
  });

  const [errors, setErrors] = useState<Errors>({});
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [states, setStates] = useState<StateType[]>([]);
  const { showToast } = useToast();
  const [isFormValid, setIsFormValid] = useState(false);
  const [companyNameExists, setCompanyNameExists] = useState<boolean>(false);
  const [checkingCompanyName, setCheckingCompanyName] = useState<boolean>(false);
  const [companyNameCheckTimeout, setCompanyNameCheckTimeout] = useState<NodeJS.Timeout | null>(null);

  // API Hooks
  const {
    data: CompanyAddData,
    error: CompanyAddError,
    loading: CompanyAddLoading,
    fetchData: CompanyAddRequestData,
  } = useAxios<any>(API_ENDPOINTS.users.COMPANY_ADD, 'POST');

  const {
    data: timeZonesData,
    error: timeZonesError,
    loading: timeZonesLoading,
    fetchData: fetchtimeZonesData,
  } = useAxios<{ data: any }>(`${API_ENDPOINTS.utility.GET_TIMEZONE}/${formData.country} `, 'GET');

  const {
    data: CompanyExistenceData,
    error: CompanyExistenceError,
    loading: CompanyExistenceLoading,
    fetchData: CompanyExistenceRequestData,
  } = useAxios<{ exists: boolean; message?: string }>(API_ENDPOINTS.users.COMPANY_EXISTENCE, 'GET');

  // Effects
  useEffect(() => {
    const loadedCountries = getAllCountries();
    setCountries(loadedCountries);
  }, []);

  useEffect(() => {
    if (formData.country) {
      // Find the country code from the country name
      const selectedCountry = countries.find((c) => c.label === formData.country);
      if (selectedCountry) {
        const loadedStates = getStatesOfCountry(selectedCountry.value);
        setStates(loadedStates);
        // Reset state when country changes
        setFormData((prev) => ({ ...prev, state: '' }));
      }
    } else {
      setStates([]);
    }
  }, [formData.country, countries]);

  useEffect(() => {
    if (formData.country) {
      fetchtimeZonesData();
    }
  }, [formData.country]);

  // Monitor API response for errors
  useEffect(() => {
    if (CompanyAddError) {
      console.error('Company Add Error:', CompanyAddError);
      showToast(CompanyAddError, 'error');
    }
  }, [CompanyAddError, showToast]);

  // Monitor API response data for error status codes
  useEffect(() => {
    if (CompanyAddData && CompanyAddData.statusCode && CompanyAddData.statusCode >= 400) {
      console.error('Company Add Error from response:', CompanyAddData);
      const errorMessage = CompanyAddData.message || 'Failed to create company';
      showToast(errorMessage, 'error');
    }
  }, [CompanyAddData, showToast]);

  // Debounced company name existence check
  const checkCompanyNameExistence = useCallback(async (companyName: string) => {
    if (!companyName || companyName.trim().length < 2) {
      setCompanyNameExists(false);
      setCheckingCompanyName(false);
      return;
    }

    setCheckingCompanyName(true);

    try {
      // Build the endpoint URL by appending company name to the base endpoint
      const endpoint = `${API_ENDPOINTS.users.COMPANY_EXISTENCE}${encodeURIComponent(companyName.trim())}`;

      const response = await apiClient({
        url: endpoint,
        method: 'GET',
        responseType: 'json',
      });

      const result = response.data;

      // Check if company exists - handle different response formats
      const companyExists = result.exists === true || result.exists === 'true' || result.data === 'exists';

      setCompanyNameExists(companyExists);
      setCheckingCompanyName(false);

      if (companyExists) {
        setErrors((prev) => ({
          ...prev,
          companyName: 'Company name already exists. Please choose a different name.',
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.companyName;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('Error checking company existence:', error);
      setCompanyNameExists(false);
      setCheckingCompanyName(false);
    }
  }, []);

  // Effect to handle company existence check with debouncing
  useEffect(() => {
    // Clear existing timeout
    if (companyNameCheckTimeout) {
      clearTimeout(companyNameCheckTimeout);
    }

    // Set new timeout for debouncing
    if (formData.companyName && formData.companyName.trim().length >= 2) {
      const timeoutId = setTimeout(() => {
        checkCompanyNameExistence(formData.companyName);
      }, 500); // 500ms debounce

      setCompanyNameCheckTimeout(timeoutId);
    } else {
      setCompanyNameExists(false);
      setCheckingCompanyName(false);
    }

    // Cleanup timeout on unmount
    return () => {
      if (companyNameCheckTimeout) {
        clearTimeout(companyNameCheckTimeout);
      }
    };
  }, [formData.companyName, checkCompanyNameExistence]);

  // Cleanup effect on unmount
  useEffect(() => {
    return () => {
      if (companyNameCheckTimeout) {
        clearTimeout(companyNameCheckTimeout);
      }
    };
  }, [companyNameCheckTimeout]);

  // Helper: normalize and validate URL (uses URL API, avoids regex issues)
  const normalizeAndValidateUrl = (rawUrl: string): string | null => {
    if (!rawUrl) return null;
    const trimmed = rawUrl.trim();

    // First try as-is (if user included scheme)
    try {
      // If this succeeds it's already a valid absolute URL
      new URL(trimmed);
      return trimmed;
    } catch {
      // If no scheme, try adding https:// and validate again
      try {
        const candidate = 'https://' + trimmed;
        new URL(candidate);
        return candidate;
      } catch {
        return null;
      }
    }
  };

  // Recompute form validity whenever mandatory fields change
  useEffect(() => {
    const mandatoryFilled =
      Boolean(formData.companyName && formData.companyName.trim()) &&
      Boolean(formData.email && formData.email.trim()) &&
      Boolean(formData.deviceTypes && formData.deviceTypes.trim()) &&
      Boolean(formData.timeZones && formData.timeZones.trim()) &&
      Boolean(formData.website && normalizeAndValidateUrl(formData.website));

    // Also check that company name doesn't exist and is not currently being checked
    const companyNameValid = !companyNameExists && !checkingCompanyName;

    setIsFormValid(mandatoryFilled && companyNameValid);
  }, [
    formData.companyName,
    formData.email,
    formData.deviceTypes,
    formData.website,
    companyNameExists,
    checkingCompanyName,
    formData.timeZones,
  ]);

  // Event Handlers
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    // clear error for that field on change
    setErrors((prev) => {
      if (!prev[name as keyof FormData]) return prev;
      const copy = { ...prev };
      delete copy[name as keyof FormData];
      return copy;
    });
  };

  const handleDropdownChange = (name: string) => (selectedValue: string) => {
    // For country field, store the full country name instead of code
    let valueToStore = selectedValue;
    if (name === 'country') {
      const selectedCountry = countries.find((c) => c.value === selectedValue);
      valueToStore = selectedCountry?.label || selectedValue;
    }

    setFormData((prev) => ({ ...prev, [name]: valueToStore }));

    // Clear error for the field on change
    setErrors((prev) => {
      if (!prev[name as keyof FormData]) return prev;
      const copy = { ...prev };
      delete copy[name as keyof FormData];
      return copy;
    });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file) {
      // File type validation
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file', 'error');
        return;
      }

      // File size validation (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size should be less than 5MB', 'error');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        selectedFile: file,
        previewUrl: previewUrl,
      }));
    }
  };

  const handleRemoveImage = () => {
    if (formData.previewUrl) {
      URL.revokeObjectURL(formData.previewUrl);
    }
    setFormData((prev) => ({
      ...prev,
      selectedFile: null,
      previewUrl: '',
    }));
  };

  const collectMissingMandatory = (): string[] => {
    const missing: string[] = [];
    if (!formData.companyName || !formData.companyName.trim()) missing.push('Company Name');
    if (!formData.timeZones || !formData.timeZones.trim()) missing.push('Time Zone');
    if (companyNameExists) missing.push('Company Name (already exists)');
    if (checkingCompanyName) missing.push('Company Name (checking availability...)');
    if (!formData.email || !formData.email.trim()) missing.push('Email');
    if (!formData.deviceTypes || !formData.deviceTypes.trim()) missing.push('Device Types');
    if (!formData.website || !normalizeAndValidateUrl(formData.website))
      missing.push('Website (valid URL required)');
    return missing;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // quick mandatory-only check (we also keep more detailed checks below)
    const missing = collectMissingMandatory();
    if (missing.length > 0) {
      // use 'warning' severity for missing-fields notification
      showToast(`Please fill mandatory fields: ${missing.join(', ')}`, 'warning');
      // also set simple errors to highlight fields
      const newErrors: Errors = {};
      if (missing.includes('Company Name')) newErrors.companyName = 'Company Name is required';
      if (missing.includes('Email')) newErrors.email = 'Email is required';
      if (missing.includes('Device Types')) newErrors.deviceTypes = 'Device Type is required';
      if (missing.includes('Country')) newErrors.country = 'Country is required';
      if (missing.includes('Time Zone')) newErrors.timeZones = 'Time Zone is required';
      if (missing.includes('Website (valid URL required)'))
        newErrors.website = 'Please enter a valid website URL';
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    // Detailed validation
    const newErrors: Errors = {};
    if (!formData.companyName) newErrors.companyName = 'Company Name is required';
    if (!formData.timeZones) newErrors.timeZones = 'Time Zone is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (companyNameExists)
      newErrors.companyName = 'Company name already exists. Please choose a different name.';
    if (checkingCompanyName) {
      showToast('Please wait while we check company name availability', 'warning');
      return;
    }
    if (!formData.email) newErrors.email = 'Email is required';

    const normalizedWebsite = normalizeAndValidateUrl(formData.website);
    if (!formData.website) {
      newErrors.website = 'Website is required';
    } else if (!normalizedWebsite) {
      newErrors.website = 'Please enter a valid website URL';
    }

    if (!formData.deviceTypes) newErrors.deviceTypes = 'Device Type is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // show consolidated toast with 'error' severity
      showToast(Object.values(newErrors).join(' | '), 'error');
      return;
    }

    setErrors({});

    // --- Build multipart FormData payload matching the curl example ---
    const fd = new FormData();

    // Company logo file (if selected)
    if (formData.selectedFile) {
      fd.append('companyLogo', formData.selectedFile);
    }

    // Basic company info
    fd.append('name', formData.companyName);
    if (formData.companyType) fd.append('type', formData.companyType);
    if (formData.description) fd.append('description', formData.description);

    // Device types - handle multiple values as separate form fields
    const deviceTypesValues = Array.isArray(formData.deviceTypes)
      ? (formData.deviceTypes as unknown as string[])
      : String(formData.deviceTypes)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

    deviceTypesValues.forEach((dt) => fd.append('deviceTypes', dt));

    // Website URLs
    fd.append('websiteUrl', normalizedWebsite as string);
    fd.append('url', normalizedWebsite as string);

    // Address fields with dot notation
    if (formData.fullAddress) fd.append('address.street', formData.fullAddress);
    if (formData.city) fd.append('address.city', formData.city);
    if (formData.state) fd.append('address.state', formData.state);
    if (formData.pincode) fd.append('address.pincode', formData.pincode);
    if (formData.country) fd.append('address.country', formData.country);
    if (formData.timeZones) fd.append('address.timeZone', formData.timeZones);

    // Contact fields with dot notation
    if (formData.landline) fd.append('contacts.telephoneNo', formData.landline);
    fd.append('contacts.email', formData.email); // mandatory field
    if (formData.mobile) fd.append('contacts.mobileNo', formData.mobile);

    // Status (always set to active)
    fd.append('status', '1');

    // Debug logging (without exposing file content)
    console.log('Submitting multipart FormData with fields:', {
      hasCompanyLogo: !!formData.selectedFile,
      companyLogoName: formData.selectedFile?.name,
      name: formData.companyName,
      type: formData.companyType,
      description: formData.description,
      deviceTypes: deviceTypesValues,
      websiteUrl: normalizedWebsite,
      addressFields: {
        street: formData.fullAddress,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        country: formData.country,
        timeZone: formData.timeZones,
      },
      contactFields: {
        email: formData.email,
        telephoneNo: formData.landline,
        mobileNo: formData.mobile,
      },
      status: '1',
    });

    try {
      // Submit the FormData using the useAxios hook
      // The Content-Type header will be automatically set by the browser with proper boundary
      await CompanyAddRequestData(fd);

      // Check if there was an error in the response
      if (CompanyAddError) {
        throw new Error(CompanyAddError);
      }

      // Check if the response indicates success
      if (CompanyAddData && CompanyAddData.statusCode && CompanyAddData.statusCode >= 400) {
        throw new Error(CompanyAddData.message || 'Failed to create company');
      }

      // success toast with severity string
      showToast('Company created successfully!', 'success');

      // navigate back after successful creation
      window.history.back();
    } catch (error: any) {
      console.error('Error submitting form:', error);

      // Extract error message from different possible error formats
      let errorMessage = 'Failed to create company. Please try again.';

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (CompanyAddError) {
        errorMessage = CompanyAddError;
      }

      // show error toast in red / with error severity
      showToast(errorMessage, 'error');

      // IMPORTANT: do NOT navigate back on failure
    }
  };

  // Render Component
  return (
    <div className="flex flex-col items-center w-full min-h-screen">
      <div className="w-full">
        {/* Back Button */}
        <div className="flex justify-start items-center mb-6">
          <Button
            className="font-medium flex items-center"
            sx={(theme: any) => ({
              color: theme.palette.primary.main,
              textTransform: 'none',
              padding: 0,
              fontSize: '16px',
            })}
            onClick={() => window.history.back()}
          >
            <img src={ArrowBackIcon} className="mr-2" width={24} height={24} alt="Back" />
            Back
          </Button>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '36px' }}>
            CREATE COMPANY
          </Typography>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Info Section */}
          <div className="mb-6">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '18px', mb: 2 }}>
              Basic Info
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <div className="relative">
                  <InputField
                    label="Company Name"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                    placeholder="Enter company name..."
                    isMandatory
                    status={
                      errors.companyName || companyNameExists
                        ? 'error'
                        : !checkingCompanyName &&
                          formData.companyName &&
                          formData.companyName.trim().length >= 2 &&
                          !companyNameExists
                        ? 'success'
                        : undefined
                    }
                    errorMessage={
                      errors.companyName ||
                      (companyNameExists
                        ? 'Company name already exists. Please choose a different name.'
                        : '')
                    }
                  />
                  {/* Custom status indicators */}
                  {checkingCompanyName && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#1976d2',
                        fontSize: '12px',
                        mt: 0.5,
                        display: 'block',
                        fontStyle: 'italic',
                      }}
                    >
                      🔍 Checking availability...
                    </Typography>
                  )}
                  {!checkingCompanyName &&
                    !companyNameExists &&
                    formData.companyName &&
                    formData.companyName.trim().length >= 2 &&
                    !errors.companyName && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#4caf50',
                          fontSize: '12px',
                          mt: 0.5,
                          display: 'block',
                          fontWeight: 500,
                        }}
                      >
                        ✓ Company name is available
                      </Typography>
                    )}
                </div>
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Type"
                  name="companyType"
                  value={formData.companyType}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter company type..."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <div className="flex flex-col gap-2">
                  <Typography variant="overline" fontWeight={600} color="secondary">
                    Capture Profile (userImage){' '}
                    {formData.selectedFile && <span className="text-green-500">✓</span>}
                  </Typography>

                  {/* Image Preview */}
                  {formData.previewUrl && (
                    <div className="relative mb-2">
                      <Box
                        sx={{
                          width: '100%',
                          height: '120px',
                          border: '2px dashed #e0e0e0',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                      >
                        <img
                          src={formData.previewUrl}
                          alt="Profile preview"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                        <Button
                          onClick={handleRemoveImage}
                          sx={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            minWidth: 'auto',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.7)',
                            },
                          }}
                        >
                          <Delete sx={{ fontSize: '16px' }} />
                        </Button>
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                        {formData.selectedFile?.name}
                      </Typography>
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className="flex flex-wrap items-center gap-2">
                    <CustomIconButton
                      icon={<img src={file} alt="upload" width={24} height={24} />}
                      text={formData.previewUrl ? 'Change Image' : 'Upload Image'}
                      background="transparent"
                      outline
                      padding="0px"
                      variant="outlined"
                      onClick={() => document.getElementById('file-upload')?.click()}
                    />

                    <input
                      type="file"
                      name="selectedFile"
                      accept="image/*"
                      id="file-upload"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />

                    {formData.previewUrl && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleRemoveImage}
                        startIcon={<Delete />}
                        sx={{ textTransform: 'none' }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  {/* Helper Text */}
                  <Typography variant="caption" color="textSecondary">
                    Supported formats: JPG, PNG, GIF • Max size: 5MB
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <InputField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter Company Description..."
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomDropDownInput
                  label="Device Types"
                  name="deviceTypes"
                  value={formData.deviceTypes}
                  onChange={handleDropdownChange('deviceTypes')}
                  options={[
                    { value: 'android', label: 'Android' },
                    { value: 'vp1', label: 'VP1' },
                  ]}
                  isMandatory
                  showDropdownArrow
                  placeholder="Select Device Types"
                />
              </Grid>
            </Grid>
          </div>

          <Divider sx={{ my: 4 }} />

          {/* Contacts Section */}
          <div className="mb-6">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '18px', mb: 2 }}>
              Contacts
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Landline Phone No"
                  name="landline"
                  value={formData.landline}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter Landline phone No..."
                  type="tel"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Mobile No"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter Mobile No..."
                  type="tel"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter Email..."
                  isMandatory
                  status={errors.email ? 'error' : 'success'}
                  errorMessage={errors.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputField
                  label="Website"
                  name="website"
                  type="text" // allow www / schemeless input
                  value={formData.website}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter website url"
                  isMandatory
                  errorMessage={errors.website}
                  status={errors.website ? 'error' : 'success'}
                />
              </Grid>
            </Grid>
          </div>

          <Divider sx={{ my: 4 }} />

          {/* Address Section */}
          <div className="mb-6">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '18px', mb: 2 }}>
              Address
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <CustomDropDownInput
                  label="Country"
                  name="country"
                  options={countries}
                  value={countries.find((c) => c.label === formData.country)?.value || ''}
                  onChange={handleDropdownChange('country')}
                  placeholder="Select Country"
                  isMandatory
                  status={errors.country ? 'error' : undefined}
                  showDropdownArrow
                />
              </Grid>
              {/* TimeZone */}
              <Grid item xs={12} md={4}>
                <CustomDropDownInput
                  label="Time Zone"
                  name="timeZones"
                  options={
                    timeZonesData?.data
                      ? Array.isArray(timeZonesData.data)
                        ? timeZonesData.data.map((tz: string) => ({ value: tz, label: tz }))
                        : []
                      : []
                  }
                  value={formData.timeZones}
                  onChange={handleDropdownChange('timeZones')}
                  placeholder="Select Time Zone"
                  isMandatory
                  status={errors.timeZones ? 'error' : undefined}
                  isDisabled={!formData.country}
                  showDropdownArrow
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomDropDownInput
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleDropdownChange('state')}
                  options={states}
                  showDropdownArrow
                  placeholder="Select State"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter City..."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter Pincode..."
                  errorMessage={errors.pincode}
                  status={errors.pincode ? 'error' : 'success'}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Full Address"
                  name="fullAddress"
                  value={formData.fullAddress}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter Company Full Address..."
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </div>

          {/* Form Actions */}
          <div className="relative w-full mt-auto">
            <div className="flex flex-col md:flex-row justify-between py-4 border-t">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => window.history.back()}
                sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '21px', textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={CompanyAddLoading || !isFormValid}
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '21px',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: 1,
                  textTransform: 'none',
                }}
              >
                {CompanyAddLoading ? 'Submitting...' : 'SUBMIT'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCompany;
